import { Skeleton } from '@/components/UI/Skeleton/Skeleton';
import { FC } from 'react';

import { useSupplierCompanyName } from '../hooks/queries/useSupplierCompanyName';

/**
 * Resolves and renders a supplier's display name from its companyId (PBI #6096).
 * Falls back to "Company #<id>" while loading fails or no name is returned, so the
 * inbox row is never blank.
 *
 * Feature #7210 / L7 fix: this per-row Company-service fetch used to run for EVERY row in
 * `PendingApprovalsInbox` — one HTTP request per row, on every page load, against a records list
 * that is now the persistent record model (DSP-tested at ~12,000 records for a single client).
 * That was fine for the old open-bundles-only inbox (short lists) but is an N+1 at this scale.
 * `GetSupplierUpdateRecordsHandler` now denormalises `supplierCompanyName` onto the record itself
 * (joined once in SQL, not per row), so `PendingApprovalsInbox` renders that directly and only
 * falls back to THIS component for a row whose name is null (an older link seeded before the
 * name existed) — see `PendingApprovalsInbox`'s `SupplierCell`. This component is no longer a
 * per-row default; it is a per-row FALLBACK, and is otherwise unchanged.
 */
export const SupplierNameCell: FC<{ supplierCompanyId: number }> = ({
  supplierCompanyId
}) => {
  const { data: name, isLoading } = useSupplierCompanyName(supplierCompanyId);

  if (isLoading) {
    return <Skeleton className="h-4 w-32" />;
  }

  return (
    <span className="font-medium text-slate-900">
      {name || `Company #${supplierCompanyId}`}
    </span>
  );
};
